import * as Complex from 'complex.js'
import * as Polynomial from 'polynomial'
import TransferFunction from './TransferFunction'
import {range} from './BodePlot'

const COMPLEX_POSITION = [4, 2, 4];

export enum RenderMode {
  COMPLEX = 1,
  POLEZERO,
  BODE,
}

/**
 * Renders a circus tent / rubber sheet visualization of a rational transfer
 * function. The plot is shown in 3D, with the complex plane along XY, and the Z
 * plane corresponding to |H(s)|. Color corresponds to ∠H(s).
 * H(s).
 *
 * Here's an example of one: https://youtu.be/bIY6ahHVgqA?t=9m4s
 */
export default class ComplexRenderer {
  tfunc: TransferFunction
  view: any
  camera: any
  graphData: any
  graphColors: any
  bodeData: any
  polesData: any
  zerosData: any

  graphSolidView: any
  graphWireView: any
  bodeView: any
  polesView: any
  zerosView: any

  min: number
  max: number
  zMin: number
  zMax: number

  selector: string

  constructor(selector: string) {
    this.selector = selector
    this.min = -1.2
    this.max = 1.2
    this.initMathbox()

    this.setRenderMode(RenderMode.COMPLEX)
  }

  setTransferFunction(tfunc: TransferFunction) {
    //console.log('setTransferFunction')
    this.tfunc = tfunc
    const view = this.view
    const xMin = this.min
    const xMax = this.max
    const yMin = this.min
    const yMax = this.max

    var [zSmallest, zBiggest] = this.calculateZRange()
    // Hardcoded for now...
    this.zMin = 0
    this.zMax = 3
    view.set("range", [[this.min, this.max], [this.zMin,this.zMax], [this.min, this.max]]);

		this.graphData.set("expr", function (emit, x, y, i, j, t) {
      emit(x, tfunc.result(x, y).abs(), y)
    })

    this.graphColors.set("expr", function (emit, x, y, i, j, t) {
      let phase = tfunc.result(x, y).arg()
      var percent = phase / (2 * Math.PI)
      var color = new THREE.Color( 0xffffff )
      color.setHSL( 1-percent, 1, 0.5 )
      emit( color.r, color.g, color.b, 1.0 )
    });

    // Set zeros and poles.
    let zeros = tfunc.getZeros()
    this.zerosData.set('width', zeros.length)
    this.zerosData.set('expr', (emit, i, t) => {
      // Mathbox sometimes calls this despite length being 0.
      if (zeros[i] === undefined) {
        return
      }
      const zero = zeros[i]
      emit(zero.re, 0, zero.im)
    })

    let poles = tfunc.getPoles()
    this.polesData.set('width', poles.length)
    this.polesData.set('expr', (emit, i, t) => {
      // Mathbox sometimes calls this despite length being 0.
      if (poles[i] === undefined) {
        return
      }
      const pole = poles[i]
      emit(pole.re, 0, pole.im)
    })
  }

  setRenderMode(mode: RenderMode) {
    switch (mode) {
      case RenderMode.COMPLEX:
        this.bodeView.set('visible', false)
        this.graphSolidView.set('visible', true)
        this.polesView.set('visible', false)
        this.zerosView.set('visible', false)
        break
      case RenderMode.BODE:
        // Render the bode plot itself, and change to a log-log scale.
        this.bodeView.set('visible', true)
        this.graphSolidView.set('visible', false)
        this.polesView.set('visible', false)
        this.zerosView.set('visible', false)
        break
      case RenderMode.POLEZERO:
        this.graphSolidView.set('visible', false)
        // Render poles and zeros as X's and O's.
        this.polesView.set('visible', true)
        this.zerosView.set('visible', true)
        break
    }
  }

  private createAxes() {
    const view = this.view

    var xAxis = view.axis( {axis: 1, width: 8, detail: 40, color:"red"} );
    var xScale = view.scale( {axis: 1, divide: 10, nice:true, zero:true} );
    var xTicks = view.ticks( {width: 5, size: 15, color: "red"} );
    var xFormat = view.format( {digits: 2, font:"Arial", weight: "bold", style: "normal", source: xScale} );
    var xTicksLabel = view.label( {color: "red", zIndex: 0, offset:[0,0], points: xScale, text: xFormat} );

    var yAxis = view.axis( {axis: 3, width: 8, detail: 40, color:"green"} );
    var yScale = view.scale( {axis: 3, divide: 5, nice:true, zero:false} );
    var yTicks = view.ticks( {width: 5, size: 15, color: "green"} );
    var yFormat = view.format( {digits: 2, font:"Arial", weight: "bold", style: "normal", source: yScale} );
    var yTicksLabel = view.label( {color: "green", zIndex: 0, offset:[0,0], points: yScale, text: yFormat} );

    var zAxis = view.axis( {axis: 2, width: 8, detail: 40, color:"blue"} );
    var zScale = view.scale( {axis: 2, divide: 5, nice:true, zero:false} );
    var zTicks = view.ticks( {width: 5, size: 15, color: "blue"} );
    var zFormat = view.format( {digits: 2, font:"Arial", weight: "bold", style: "normal", source: zScale} );
    var zTicksLabel = view.label( {color: "blue", zIndex: 0, offset:[0,0], points: zScale, text: zFormat} );
  }

  private initMathbox() {
    const el = document.querySelector(this.selector)
    const height = el.offsetWidth * 0.75
    el.style.height = `${height}px`
    const mathbox = mathBox({
      element: el,
      plugins: ['core', 'controls', 'cursor', 'mathbox'],
      controls: {klass: THREE.OrbitControls}
    })

    if (mathbox.fallback) {
      throw "WebGL not supported"
    }

    const three = mathbox.three;
    three.renderer.setClearColor(new THREE.Color(0xFFFFFF), 1.0)

    const camera = mathbox.camera({ proxy: true, position: COMPLEX_POSITION, lookAt: [0, 0, 0] })
    const view = mathbox.cartesian({
      range: [[this.min, this.max], [this.min, this.max], [this.min,this.max]],
      scale: [2, 1, 2],
    })

    this.view = view
    this.camera = camera

    this.createAxes()
    view.grid( {axes:[1,3], width: 2, divideX: 20, divideY: 20, opacity:0.25} );

    // Setup graphData and graphColors, but set real data later.
    this.graphData = view.area({
      axes: [1,3], channels: 3, width: 64, height: 64,
      expr: function (emit, x, y, i, j, t) {
        var z = x * y;
        emit(x, z, y);
      },
    });

    this.graphColors = view.area({
      expr: function (emit, x,y, i,j, t) {
        if (x < 0)
          emit(1.0, 0.0, 0.0, 1.0);
        else
          emit(0.0, 1.0, 0.0, 1.0);
      },
      axes: [1,3],
      width:  64, height: 64,
      channels: 4, // RGBA
    });

    // How many points to use to draw the unit circle.
    this.bodeData = view.interval({
      expr: (emit, theta, i, t) => {
        const c = new Complex('i').mul(theta).exp()
        const x = c.re
        const y = c.im
        let z = 0;
        if (this.tfunc) {
          z = this.tfunc.result(x, y).abs()
        }
        emit(x, z, y);
      },
      channels: 3,
      width: 64,
      range: [0, 2*Math.PI],
    });

    this.zerosData = view.array({
      expr: (emit, i, t) => {
        emit(null)
      },
      width: 0,
    })

    this.polesData = view.array({
      expr: (emit, i, t) => {
        emit(null)
      },
      width: 0,
    })

    // Create graph in two parts, because want solid and wireframe to be
    // different colors.
    this.graphSolidView = view.surface({
      points: this.graphData,
      color:"#FFFFFF", shaded:false, fill:true, lineX:false, lineY:false, colors: this.graphColors, width:0,
    });

    this.graphWireView = view.surface({
      points: this.graphData,
      color:"#000000", shaded:false, fill:false, lineX:true, lineY:true, width:2,
    });

    // Create a bode plot.
    this.bodeView = view.line({
      points: this.bodeData,
      color: 0x000000,
      width: 10,
    });

    // Plot the zeros.
    this.zerosView = view.point({
      points: this.zerosData,
      color: 0x000000,
      size: 100,
      shape: 'circle',
      fill: false,
    })
    this.polesView = view.point({
      points: this.polesData,
      color: 0x000000,
      size: 100,
      // TODO: Make this into a cross.
      shape: 'cross',
      fill: false,
    })
  }

  private calculateZRange() {
    const tfunc = this.tfunc
    const range = this.max - this.min
    const step = range / 256
    var zSmallest = tfunc.result(this.min, this.max).abs();
    var zBiggest  = tfunc.result(this.min, this.max).abs();
    for (var x = this.min; x <= this.max; x += step)
    {
      for (var y = this.min; y <= this.max; y += step)
      {
        var z = tfunc.result(x, y).abs();
        if (z < zSmallest) zSmallest = z;
        if (z > zBiggest)  zBiggest  = z;
      }
    }
    return [zSmallest, zBiggest]
  }
}



// WEBPACK FOOTER //
// ./src/ComplexRenderer.ts